// Calendar, Brain Dump, AI Organizer, Reflection, Dashboard, Settings, Login, Blocks
import React from 'react';
import { Icon, Pyramid } from './icons.jsx';
import { PROJECTS, BLOCKS, BRAIN_DUMPS, TOP3, blockColor } from './data.js';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const CalendarView = () => {
  const [mode, setMode] = React.useState("Month");
  const monthName = "May 2026";
  // Build 5x7 with May 2026: May 1 2026 = Friday
  const days = [];
  const offset = 4; // Fri=col 4 (Mon=0)
  for (let i = 0; i < 35; i++) {
    const dn = i - offset + 1;
    const inMonth = dn >= 1 && dn <= 31;
    days.push({ n: inMonth ? dn : (dn < 1 ? 26 + dn : dn - 31), inMonth, today: dn === 8 });
  }
  // colored bars per day
  const dayBars = {
    1: [{ c: "var(--p-1)" }, { c: "var(--cat-work)" }],
    2: [{ c: "var(--p-2)" }],
    5: [{ c: "var(--p-1)" }, { c: "var(--p-3)" }, { c: "var(--cat-fitness)" }],
    6: [{ c: "var(--p-1)" }, { c: "var(--cat-work)" }],
    7: [{ c: "var(--p-3)" }, { c: "var(--p-7)" }, { c: "var(--cat-family)" }],
    8: [{ c: "var(--p-1)" }, { c: "var(--p-2)" }, { c: "var(--cat-work)" }, { c: "var(--cat-social)" }],
    11: [{ c: "var(--p-3)" }, { c: "var(--cat-fitness)" }],
    12: [{ c: "var(--p-1)" }, { c: "var(--cat-work)" }],
    13: [{ c: "var(--p-2)" }, { c: "var(--p-7)" }],
    14: [{ c: "var(--p-1)" }, { c: "var(--cat-fitness)" }],
    15: [{ c: "var(--p-3)" }, { c: "var(--cat-social)" }],
    19: [{ c: "var(--p-1)" }, { c: "var(--p-5)" }],
    20: [{ c: "var(--p-2)" }],
    21: [{ c: "var(--p-1)" }, { c: "var(--cat-fitness)" }],
    22: [{ c: "var(--p-3)" }, { c: "var(--cat-social)" }],
    25: [{ c: "var(--p-7)" }],
    26: [{ c: "var(--p-1)" }, { c: "var(--cat-work)" }],
    27: [{ c: "var(--p-2)" }, { c: "var(--cat-fitness)" }],
    28: [{ c: "var(--p-1)" }, { c: "var(--p-3)" }],
    29: [{ c: "var(--cat-social)" }],
  };

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">May, <em>2026</em></h1>
          <div className="page__sub">
            <span className="mono">128 cycles scheduled · 41 logged · 9 days into the month</span>
          </div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="tabs">
            {["Day", "Week", "Month", "Year"].map(m => (
              <button key={m} data-active={mode === m ? "1" : "0"} onClick={() => setMode(m)}>{m}</button>
            ))}
          </div>
          <button className="btn btn--outline btn--sm"><Icon name="chev_left" size={13} /></button>
          <button className="btn btn--outline btn--sm">Today</button>
          <button className="btn btn--outline btn--sm"><Icon name="chev_right" size={13} /></button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 20 }}>
        <div className="cal">
          <div className="cal__head">
            {["MON","TUE","WED","THU","FRI","SAT","SUN"].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="cal__grid">
            {days.map((d, i) => {
              const bars = d.inMonth ? (dayBars[d.n] || []) : [];
              return (
                <div key={i} className={"cal__day" + (!d.inMonth ? " cal__day--out" : "") + (d.today ? " cal__day--today" : "")}>
                  <div className="num">
                    <strong>{d.n}</strong>
                    {d.today && <span className="mono" style={{ fontSize: 9, color: "var(--accent)", letterSpacing: "0.1em" }}>NOW</span>}
                  </div>
                  {bars.slice(0, 3).map((b, j) => (
                    <div key={j} className="cal__bar" style={{ background: b.c, color: "white", opacity: 0.92 }}>
                      <span className="dot" style={{ background: "white", opacity: 0.7 }}></span>
                    </div>
                  ))}
                  {bars.length > 3 && (
                    <div className="mono" style={{ fontSize: 10, color: "var(--ink-3)", padding: "0 4px" }}>+{bars.length - 3} more</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Block templates rail */}
        <div>
          <h3 style={{ fontSize: 11, fontWeight: 500, fontFamily: "var(--font-mono)", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-3)", margin: "8px 0 12px" }}>
            Block templates · drag onto a day
          </h3>
          <div className="col" style={{ gap: 8 }}>
            {BLOCKS.map(b => (
              <div key={b.id} className="block-card">
                <div className="block-card__color" style={{ background: blockColor(b.category) }}>
                  <Icon name={b.icon} size={14} stroke={2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{b.name}</div>
                  <div className="meta">{b.cycles} cyc · {b.recur}</div>
                </div>
                <Icon name="drag" size={14} stroke={1.4} />
              </div>
            ))}
            <button className="block-card" style={{ borderStyle: "dashed", color: "var(--ink-3)", justifyContent: "center" }}>
              <Icon name="plus" size={14} /><span>New block template</span>
            </button>
          </div>

          <h3 style={{ fontSize: 11, fontWeight: 500, fontFamily: "var(--font-mono)", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-3)", margin: "24px 0 12px" }}>
            Legend
          </h3>
          <div className="col" style={{ gap: 6 }}>
            {[
              { c: "var(--cat-work)", n: "Work / shifts" },
              { c: "var(--cat-family)", n: "Family" },
              { c: "var(--cat-fitness)", n: "Fitness" },
              { c: "var(--cat-rest)", n: "Rest" },
              { c: "var(--cat-social)", n: "Social" },
            ].map(l => (
              <div key={l.n} className="row" style={{ fontSize: 12 }}>
                <span className="pdot" style={{ background: l.c, width: 10, height: 10 }}></span>
                <span style={{ color: "var(--ink-2)" }}>{l.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// BLOCKS view
const BlocksView = () => (
  <div className="page">
    <div className="page__head">
      <div>
        <h1 className="page__title">Block <em>templates</em></h1>
        <div className="page__sub">Time commitments — work shifts, family, fitness — that aren't projects but occupy cycles</div>
      </div>
      <button className="btn btn--accent"><Icon name="plus" size={14} /><span>New template</span></button>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
      {BLOCKS.map(b => (
        <div key={b.id} className="card card--hov" style={{ padding: 18 }}>
          <div className="row" style={{ gap: 12, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: blockColor(b.category), display: "grid", placeItems: "center", color: "white" }}>
              <Icon name={b.icon} size={18} stroke={1.8} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{b.name}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "capitalize" }}>{b.category}</div>
            </div>
            <button className="iconbtn"><Icon name="more" size={14} /></button>
          </div>
          <div className="row" style={{ fontSize: 12.5, color: "var(--ink-2)", gap: 8 }}>
            <Icon name="clock" size={12} /> {b.time}
          </div>
          <div className="row" style={{ fontSize: 12.5, color: "var(--ink-2)", gap: 8, marginTop: 4 }}>
            <Icon name="refresh" size={12} /> {b.recur}
          </div>
          <div className="divider" style={{ margin: "12px 0" }}></div>
          <div className="row">
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{b.cycles} cycle{b.cycles > 1 ? "s" : ""}</span>
            <button className="btn btn--ghost btn--sm" style={{ marginLeft: "auto" }}><Icon name="calendar" size={12} /><span>Apply this week</span></button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// BRAIN DUMP
const BrainDumpView = () => {
  const [items, setItems] = React.useState(BRAIN_DUMPS);
  const [removing, setRemoving] = React.useState(null);
  const [val, setVal] = React.useState("");

  const remove = (id) => {
    setRemoving(id);
    setTimeout(() => setItems(xs => xs.filter(x => x.id !== id)), 240);
  };
  const add = () => {
    if (!val.trim()) return;
    setItems(xs => [{ id: Date.now(), body: val, time: "now" }, ...xs]);
    setVal("");
  };

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Brain <em>dump</em></h1>
          <div className="page__sub">Everything in. Triage and convert later. <span className="mono">{items.length} in inbox</span></div>
        </div>
        <button className="btn btn--accent">
          <Icon name="sparkles" size={14} /><span>AI triage all</span>
        </button>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 24 }}>
        <textarea className="textarea" placeholder="What's on your mind? — anything goes. Will land in your inbox to triage later."
          rows={3} value={val} onChange={e => setVal(e.target.value)} />
        <div className="row" style={{ marginTop: 10 }}>
          <button className="btn btn--ghost btn--sm"><Icon name="mic" size={13} /><span>Voice memo</span></button>
          <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-3)" }}>⌘ Enter to capture</span>
          <button className="btn btn--primary" onClick={add} disabled={!val.trim()}>Capture</button>
        </div>
      </div>

      <div className="col" style={{ gap: 12 }}>
        {items.map(d => (
          <div key={d.id} className={"bd-card" + (removing === d.id ? " removing" : "")}>
            <div className="row">
              <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{d.time} ago</span>
              <span className="chip" style={{ marginLeft: "auto" }}>Inbox</span>
            </div>
            <div className="body">{d.body}</div>
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              <button className="btn btn--outline btn--sm" onClick={() => remove(d.id)}><Icon name="layers" size={12} /><span>Project</span></button>
              <button className="btn btn--outline btn--sm" onClick={() => remove(d.id)}><Icon name="check" size={12} /><span>Task</span></button>
              <button className="btn btn--outline btn--sm" onClick={() => remove(d.id)}><Icon name="flag" size={12} /><span>Milestone</span></button>
              <button className="btn btn--outline btn--sm" onClick={() => remove(d.id)}><Icon name="tag" size={12} /><span>Block</span></button>
              <button className="btn btn--outline btn--sm" onClick={() => remove(d.id)}><Icon name="book" size={12} /><span>Reflection</span></button>
              <span style={{ flex: 1 }}></span>
              <button className="btn btn--ghost btn--sm" onClick={() => remove(d.id)}>Park</button>
              <button className="btn btn--ghost btn--sm" onClick={() => remove(d.id)}><Icon name="trash" size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// AI ORGANIZER
const AIOrganizerView = () => (
  <div className="page">
    <div className="page__head">
      <div>
        <h1 className="page__title">AI <em>organizer</em></h1>
        <div className="page__sub">Drafts proposals · you commit · nothing saves until you say so</div>
      </div>
      <span className="chip"><Icon name="sparkles" size={11} />claude-opus-4-7</span>
    </div>

    <div className="card" style={{ padding: 24, marginBottom: 24 }}>
      <label className="label">Project description</label>
      <textarea className="textarea" rows={5}
        defaultValue="I want to restore my '67 Mustang in time for September. Body work first — passenger fender has rust, driver door is dented. Then a respray in the original Acapulco Blue. Interior is a separate phase: ivory leather, new headliner, dash refinished. Goal is rolling and roadworthy by Labor Day weekend." />

      <div className="row" style={{ marginTop: 12 }}>
        <div className="col" style={{ gap: 6 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Total cycles</span>
          <input className="input" defaultValue="200" style={{ width: 100 }} />
        </div>
        <div className="col" style={{ gap: 6, marginLeft: 16 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Per week</span>
          <input className="input" defaultValue="4" style={{ width: 100 }} />
        </div>
        <button className="btn btn--accent" style={{ marginLeft: "auto" }}>
          <Icon name="sparkles" size={14} /><span>Organize</span>
        </button>
      </div>
    </div>

    <div className="proposal" style={{ padding: 24, marginBottom: 16 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <span className="spark"><Icon name="sparkles" size={11} />Proposal · 5 segments · 18 tasks · ~196 cycles</span>
        <button className="btn btn--ghost btn--sm" style={{ marginLeft: "auto" }}>Why this?</button>
      </div>
      <div className="serif-it" style={{ fontSize: 18, lineHeight: 1.4, color: "var(--ink-1)", marginBottom: 16 }}>
        "Restore a '67 Mustang to roadworthy by Labor Day — body, paint, interior, mechanical."
      </div>

      {[
        { title: "Body & rust repair", n: 4, c: 38, tasks: ["Strip passenger fender", "Cut & weld replacement panel", "Pull driver door dent", "Bondo + sand to 220"] },
        { title: "Prep & paint — Acapulco Blue", n: 4, c: 56, tasks: ["Mask trim and glass", "Etch primer 2 coats", "Color 3 coats", "Clear 2 coats + wet sand"] },
        { title: "Interior — ivory leather", n: 4, c: 48, tasks: ["Strip seats and panels", "Send foam for re-cushion", "Install headliner", "Reinstall trim"] },
        { title: "Dash + electrical", n: 3, c: 24, tasks: ["Refinish dash pad", "Replace gauge cluster", "Wire stereo + speakers"] },
        { title: "Mechanical shake-down", n: 3, c: 30, tasks: ["Brake bleed", "Coolant flush", "Road test 50 miles"] },
      ].map((s, i) => (
        <div key={i} style={{ padding: "14px 0", borderTop: i === 0 ? 0 : "1px solid var(--hair)" }}>
          <div className="row" style={{ marginBottom: 6 }}>
            <span style={{
              width: 22, height: 22, borderRadius: 6, background: "var(--accent)", color: "white",
              fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600,
              display: "grid", placeItems: "center"
            }}>{i + 1}</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{s.title}</span>
            <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-3)" }}>{s.n} tasks · {s.c} cycles</span>
          </div>
          <div style={{ paddingLeft: 30, color: "var(--ink-2)", fontSize: 12.5, lineHeight: 1.7 }}>
            {s.tasks.map((t, j) => <div key={j}>• {t}</div>)}
          </div>
        </div>
      ))}

      <div className="row" style={{ marginTop: 16, gap: 8 }}>
        <button className="btn btn--accent">Accept all</button>
        <button className="btn btn--outline">Edit before saving</button>
        <button className="btn btn--ghost">Reject</button>
      </div>
    </div>
  </div>
);

// REFLECTION
const ReflectionView = () => (
  <div className="page">
    <div className="page__head">
      <div>
        <h1 className="page__title">Daily <em>reflection</em></h1>
        <div className="page__sub">Friday · May 8 · written 4 of last 7 days</div>
      </div>
      <button className="btn btn--outline btn--sm"><Icon name="chart" size={13} /><span>Streak history</span></button>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 24 }}>
      <div className="card" style={{ padding: 24 }}>
        <div className="row" style={{ marginBottom: 16 }}>
          <div className="ring-prog" style={{ "--p": 56 }}><span>9·16</span></div>
          <div className="col" style={{ gap: 2, marginLeft: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Today's snapshot</span>
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>5 cycles done · 4 ahead · 1 carry to tomorrow</span>
          </div>
          <button className="btn btn--ghost btn--sm" style={{ marginLeft: "auto" }}><Icon name="mic" size={13} /><span>Voice</span></button>
        </div>

        <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "0 0 6px" }}>What went well</h3>
        <textarea className="textarea" rows={3} defaultValue="Actually shipped the Pipeline onboarding states this morning — the sticky one for two weeks. The early lift made the whole afternoon feel ahead of schedule." />

        <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "20px 0 6px" }}>What didn't</h3>
        <textarea className="textarea" rows={3} defaultValue="Lost cycle 11 to email — should have flagged it as Top 3 yesterday so I'd have noticed it earlier in the day." />

        <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "20px 0 6px" }}>One thing for tomorrow</h3>
        <input className="input" defaultValue="Mustang fender — finish the bondo pass before the morning lift" />

        <div className="row" style={{ marginTop: 20, gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn--ghost btn--sm">Save draft</button>
          <button className="btn btn--accent">Save reflection</button>
        </div>
      </div>

      <div>
        <h3 style={{ fontSize: 11, fontWeight: 500, fontFamily: "var(--font-mono)", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-3)", margin: "8px 0 12px" }}>This week</h3>
        <div className="col" style={{ gap: 6 }}>
          {[
            { d: "Fri · 5/8", w: "Today", on: true, p: 56 },
            { d: "Thu · 5/7", w: "Pipeline launch sprint", on: true, p: 88 },
            { d: "Wed · 5/6", w: "—", on: false, p: 0 },
            { d: "Tue · 5/5", w: "Hiring loop debrief", on: true, p: 72 },
            { d: "Mon · 5/4", w: "Mustang fender start", on: true, p: 64 },
            { d: "Sun · 5/3", w: "—", on: false, p: 0 },
            { d: "Sat · 5/2", w: "Recovery day stamp", on: true, p: 28 },
          ].map((r, i) => (
            <div key={i} className="card" style={{ padding: 12 }}>
              <div className="row" style={{ gap: 8 }}>
                <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", width: 64 }}>{r.d}</span>
                <span style={{ fontSize: 12.5, color: r.on ? "var(--ink-1)" : "var(--ink-4)", flex: 1, fontStyle: r.on ? "normal" : "italic" }}>{r.on ? r.w : "no entry"}</span>
                {r.on && <Icon name="check" size={11} stroke={2.6} style={{ color: "var(--success)" }} />}
              </div>
              {r.on && <div className="bar" style={{ marginTop: 6 }}><i style={{ width: r.p + "%" }}></i></div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// DASHBOARD
const Dashboard = ({ onOpen }) => (
  <div className="page">
    <div className="page__head">
      <div>
        <h1 className="page__title">Good morning, <em>Ed</em></h1>
        <div className="page__sub">Friday, May 8 · 9 awake cycles ahead · weekly review on Sunday</div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn--outline btn--sm"><Icon name="sparkles" size={13} /><span>Distribute next week</span></button>
        <button className="btn btn--accent"><Icon name="play" size={13} /><span>Start cycle 9</span></button>
      </div>
    </div>

    <div className="kpis">
      {[
        { l: "This week", v: 14, vs: "/32", d: "+4 vs last week", up: true, p: 44 },
        { l: "Active projects", v: 8, vs: "", d: "3 high · 3 med · 2 low", up: false, p: 60 },
        { l: "Reflection streak", v: 4, vs: "/7", d: "best: 11 days", up: false, p: 57 },
        { l: "Cycle hit-rate", v: "78%", vs: "", d: "↑ from 71% last wk", up: true, p: 78 },
      ].map(k => (
        <div key={k.l} className="kpi">
          <span className="kpi__lbl">{k.l}</span>
          <span className="kpi__val tnum">{k.v}<span style={{ color: "var(--ink-4)", fontSize: 18 }}>{k.vs}</span></span>
          <span className={"kpi__delta " + (k.up ? "kpi__delta--up" : "")}>{k.d}</span>
          <div className="kpi__bar"><i style={{ width: k.p + "%" }}></i></div>
        </div>
      ))}
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20, marginTop: 8 }}>
      <div className="card">
        <div className="card__head">
          <Icon name="target" size={14} />
          <div className="card__title">Top 3 — <em>today</em></div>
          <span className="card__sub" style={{ marginLeft: "auto" }}>1 / 3 done</span>
        </div>
        {TOP3.map((t, i) => (
          <div key={t.id} className="row" style={{ padding: "12px 24px", borderTop: i === 0 ? 0 : "1px solid var(--hair)", gap: 12 }}>
            <span className="tick" data-checked={t.done ? "1" : "0"}><Icon name="check" size={11} stroke={2.6} /></span>
            <span style={{ flex: 1, fontSize: 13.5, color: t.done ? "var(--ink-3)" : "var(--ink-1)", textDecoration: t.done ? "line-through" : "none" }}>{t.title}</span>
            <span className="pdot" style={{ background: t.color }}></span>
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{t.project}</span>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card__head">
          <Icon name="layers" size={14} />
          <div className="card__title">Projects pulse</div>
        </div>
        {PROJECTS.slice(0, 5).map((p, i) => {
          const pct = Math.round((p.logged / p.target) * 100);
          return (
            <button key={p.id} onClick={() => onOpen(p)} className="row" style={{ width: "100%", textAlign: "left", padding: "12px 24px", borderTop: i === 0 ? 0 : "1px solid var(--hair)", gap: 12 }}>
              <span className="pdot" style={{ background: p.color }}></span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
              <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", width: 60, textAlign: "right" }}>{pct}%</span>
              <div className="bar" style={{ width: 80 }}><i style={{ width: pct + "%", background: p.color }}></i></div>
            </button>
          );
        })}
      </div>
    </div>

    <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "32px 0 12px" }}>Last 14 days · cycles logged</h3>
    <div className="card" style={{ padding: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(14, 1fr)", gap: 4, alignItems: "end", height: 96 }}>
        {[3,5,4,7,2,6,8,5,4,9,7,5,6,5].map((v, i) => (
          <div key={i} style={{
            height: (v / 9) * 100 + "%",
            background: i === 13 ? "var(--accent)" : "var(--surface-3)",
            borderRadius: "4px 4px 0 0",
            minHeight: 6,
            transition: "all 0.6s var(--ease-out)"
          }}></div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(14, 1fr)", marginTop: 8 }}>
        {[..."MTWTFSSMTWTFS"].slice(0, 14).map((d, i) => (
          <span key={i} className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)", textAlign: "center" }}>{d}</span>
        ))}
      </div>
    </div>
  </div>
);

// SETTINGS
const SettingsView = ({ theme, setTheme }) => {
  const [wake, setWake] = React.useState("06:00");
  const [sleep, setSleep] = React.useState(450);
  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title"><em>Settings</em></h1>
          <div className="page__sub">Two values run everything else: when you wake, and how long you sleep.</div>
        </div>
      </div>

      <div className="col" style={{ gap: 16 }}>
        <SettingsRow title="Cycle anchor" desc="Cycle 1 begins at wake. Sleep cycles fill the tail end of the day.">
          <div className="row" style={{ gap: 12 }}>
            <div>
              <label className="label">Wake time</label>
              <input className="input" type="time" value={wake} onChange={e => setWake(e.target.value)} style={{ width: 120 }} />
            </div>
            <div>
              <label className="label">Sleep duration</label>
              <select className="select" value={sleep} onChange={e => setSleep(+e.target.value)} style={{ width: 160 }}>
                <option value={360}>6 hours · 4 cycles</option>
                <option value={450}>7.5 hours · 5 cycles</option>
                <option value={540}>9 hours · 6 cycles</option>
              </select>
            </div>
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Awake budget</div>
              <div style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.02em" }}>11 cycles · <span className="serif-it">16.5h</span></div>
            </div>
          </div>
        </SettingsRow>

        <SettingsRow title="Appearance" desc="Light reads better in daylight; dark reduces eye strain after sundown.">
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn--outline btn--sm" data-active={theme === "light"} onClick={() => setTheme("light")}
              style={theme === "light" ? { borderColor: "var(--accent)", color: "var(--accent)" } : null}>
              <Icon name="sun" size={13} /><span>Light</span>
            </button>
            <button className="btn btn--outline btn--sm" data-active={theme === "dark"} onClick={() => setTheme("dark")}
              style={theme === "dark" ? { borderColor: "var(--accent)", color: "var(--accent)" } : null}>
              <Icon name="moon" size={13} /><span>Dark</span>
            </button>
          </div>
        </SettingsRow>

        <SettingsRow title="Default landing" desc="Where you land when you open the app.">
          <div className="row" style={{ gap: 6 }}>
            {["Today", "Dashboard"].map(o => (
              <button key={o} className="btn btn--outline btn--sm" style={o === "Today" ? { borderColor: "var(--accent)", color: "var(--accent)" } : null}>{o}</button>
            ))}
          </div>
        </SettingsRow>

        <SettingsRow title="Energy preference" desc="Tells the Cycle Feeding Engine what to schedule first thing.">
          <select className="select" defaultValue="deep_work" style={{ width: 200 }}>
            <option value="deep_work">Deep work mornings</option>
            <option value="admin">Admin first</option>
            <option value="mixed">Mixed</option>
          </select>
        </SettingsRow>

        <SettingsRow title="GitHub" desc="Link projects to repos. Tasks ↔ issues, segments ↔ milestones.">
          <div className="row" style={{ gap: 12 }}>
            <span className="chip chip--success"><Icon name="check" size={11} stroke={2.6} />Connected as <strong>@edgabriel</strong></span>
            <button className="btn btn--ghost btn--sm">Disconnect</button>
          </div>
        </SettingsRow>

        <SettingsRow title="AI provider" desc="Anthropic Claude. Opus for organize, Haiku for triage. Falls back to deterministic heuristics if the API hiccups.">
          <div className="row" style={{ gap: 12 }}>
            <select className="select" defaultValue="anthropic" style={{ width: 180 }}>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
            </select>
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>used 14 / 60 today</span>
          </div>
        </SettingsRow>

        <SettingsRow title="Successful Citizen Suite" desc="ProjectOS is module #1. Other modules sync via CitizenCloudOS once it's live.">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="chip" style={{ background: "var(--accent-soft)", color: "var(--accent)", borderColor: "transparent" }}><Pyramid size={11} /> ProjectOS · active</span>
            <span className="chip">Pipeline · phase 2</span>
            <span className="chip">Relay · phase 2</span>
            <span className="chip">LifeOS · phase 3</span>
            <span className="chip">CCOS · phase 3</span>
          </div>
        </SettingsRow>
      </div>
    </div>
  );
};

const SettingsRow = ({ title, desc, children }) => (
  <div className="card" style={{ padding: 24, display: "grid", gridTemplateColumns: "280px 1fr", gap: 32, alignItems: "center" }}>
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5 }}>{desc}</div>
    </div>
    <div>{children}</div>
  </div>
);

// LOGIN
const LoginView = ({ onSignIn }) => (
  <div style={{
    minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr 1fr",
    background: "var(--bg)"
  }}>
    <div style={{ display: "grid", placeItems: "center", padding: 40 }}>
      <div style={{ width: "100%", maxWidth: 360 }}>
        <div className="row" style={{ gap: 10, marginBottom: 36 }}>
          <span style={{ color: "var(--accent)" }}><Pyramid size={28} /></span>
          <span style={{ fontFamily: "var(--font-serif)", fontSize: 22 }}>Project<em style={{ fontSize: 24 }}>OS</em></span>
        </div>
        <h1 style={{ fontSize: 32, letterSpacing: "-0.025em", fontWeight: 500, margin: "0 0 8px" }}>Welcome <em>back</em></h1>
        <p style={{ color: "var(--ink-3)", fontSize: 14, marginBottom: 28 }}>Organize your chaos. Sign in to your workspace.</p>

        <div className="col" style={{ gap: 14 }}>
          <div>
            <label className="label">Email</label>
            <input className="input" defaultValue="ed@citizenprojectos.com" />
          </div>
          <div>
            <label className="label">Password</label>
            <input className="input" type="password" defaultValue="••••••••••" />
          </div>
          <div className="row">
            <label className="row" style={{ gap: 6, fontSize: 12.5, color: "var(--ink-3)" }}>
              <input type="checkbox" defaultChecked /> Remember me
            </label>
            <a className="mono" style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--accent)" }}>Forgot password?</a>
          </div>
          <button className="btn btn--accent btn--lg" style={{ justifyContent: "center", width: "100%" }} onClick={onSignIn}>
            Sign in <Icon name="arrow_right" size={14} />
          </button>
          <div className="row" style={{ gap: 10, margin: "8px 0", color: "var(--ink-4)", fontSize: 11 }}>
            <div style={{ flex: 1, height: 1, background: "var(--hair)" }}></div>
            <span className="mono">OR</span>
            <div style={{ flex: 1, height: 1, background: "var(--hair)" }}></div>
          </div>
          <button className="btn btn--outline btn--lg" style={{ justifyContent: "center", width: "100%" }}>
            <Icon name="github" size={14} /><span>Continue with GitHub</span>
          </button>
        </div>

        <div style={{ marginTop: 32, fontSize: 12, color: "var(--ink-3)", textAlign: "center" }}>
          Module #1 of the <span style={{ color: "var(--ink-1)", fontFamily: "var(--font-serif)", fontStyle: "italic" }}>Successful Citizen</span> Suite
        </div>
      </div>
    </div>

    <div style={{
      background: "linear-gradient(155deg, oklch(54% 0.19 268) 0%, oklch(36% 0.16 290) 50%, oklch(20% 0.04 270) 100%)",
      position: "relative", overflow: "hidden",
      display: "grid", placeItems: "center"
    }}>
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(circle at 30% 20%, oklch(70% 0.20 268 / 0.4), transparent 50%)"
      }}></div>
      <div style={{ position: "relative", color: "white", padding: 40, maxWidth: 480 }}>
        <div className="serif-it" style={{ fontSize: 36, lineHeight: 1.2, letterSpacing: "-0.02em", marginBottom: 18 }}>
          "Turn the chaos of running 20 businesses plus a real life into a single ordered system."
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}>
          — the ProjectOS thesis · v1.3
        </div>

        <div style={{ marginTop: 64, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ height: 4, background: "rgba(255,255,255,0.4)", borderRadius: 2 }}>
              <div style={{ width: i === 1 ? "100%" : "0", height: "100%", background: "white", borderRadius: 2, transition: "width 1s var(--ease-out)" }}></div>
            </div>
          ))}
        </div>
        <div className="row" style={{ marginTop: 12, gap: 24, fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.7 }}>
          <span>Phase 1 · ship</span>
          <span>Pipeline</span>
          <span>LifeOS</span>
          <span>CCOS</span>
        </div>
      </div>
    </div>
  </div>
);

export {
  CalendarView,
  BlocksView,
  BrainDumpView,
  AIOrganizerView,
  ReflectionView,
  Dashboard,
  SettingsView,
  LoginView,
};
